/**
 * timezone.ts
 * Comprehensive timezone options for international traders.
 *
 * IANA timezone identifiers with UTC offset labels for display.
 * Used by onboarding (BasicSettingsStep) and clock widget settings.
 *
 * @see components/Onboarding/steps/BasicSettingsStep.tsx — onboarding timezone selector
 * @see components/TradingHub/LiveTrading/widgets/TimeOfDayClock.tsx — clock settings popover
 */

// ===== TYPES =====

/** A timezone option for display in selectors */
export interface TimezoneOption {
  /** IANA timezone identifier (e.g., "America/New_York") */
  value: string;
  /** Display label with UTC offset (e.g., "(UTC-4) New York") */
  label: string;
}

// ===== DATA =====

/**
 * Comprehensive timezone options for international traders.
 * Ordered by UTC offset (west to east), then alphabetically within each offset group.
 */
export const TIMEZONES = [
  // UTC
  { value: 'UTC', label: '(UTC) UTC' },

  // Americas - North America
  { value: 'Pacific/Honolulu', label: '(UTC-10) Honolulu' },
  { value: 'America/Anchorage', label: '(UTC-8) Anchorage' },
  { value: 'America/Juneau', label: '(UTC-8) Juneau' },
  { value: 'America/Los_Angeles', label: '(UTC-7) Los Angeles' },
  { value: 'America/Phoenix', label: '(UTC-7) Phoenix' },
  { value: 'America/Vancouver', label: '(UTC-7) Vancouver' },
  { value: 'America/Denver', label: '(UTC-6) Denver' },
  { value: 'America/Mexico_City', label: '(UTC-6) Mexico City' },
  { value: 'America/El_Salvador', label: '(UTC-6) San Salvador' },
  { value: 'America/Bogota', label: '(UTC-5) Bogota' },
  { value: 'America/Chicago', label: '(UTC-5) Chicago' },
  { value: 'America/Lima', label: '(UTC-5) Lima' },
  { value: 'America/Caracas', label: '(UTC-4) Caracas' },
  { value: 'America/New_York', label: '(UTC-4) New York' },
  { value: 'America/Toronto', label: '(UTC-4) Toronto' },
  { value: 'America/Buenos_Aires', label: '(UTC-3) Buenos Aires' },
  { value: 'America/Santiago', label: '(UTC-3) Santiago' },
  { value: 'America/Sao_Paulo', label: '(UTC-3) Sao Paulo' },

  // Europe & Africa
  { value: 'Atlantic/Azores', label: '(UTC) Azores' },
  { value: 'Atlantic/Reykjavik', label: '(UTC) Reykjavik' },
  { value: 'Africa/Casablanca', label: '(UTC+1) Casablanca' },
  { value: 'Europe/Dublin', label: '(UTC+1) Dublin' },
  { value: 'Africa/Lagos', label: '(UTC+1) Lagos' },
  { value: 'Europe/Lisbon', label: '(UTC+1) Lisbon' },
  { value: 'Europe/London', label: '(UTC+1) London' },
  { value: 'Africa/Tunis', label: '(UTC+1) Tunis' },
  { value: 'Europe/Amsterdam', label: '(UTC+2) Amsterdam' },
  { value: 'Europe/Belgrade', label: '(UTC+2) Belgrade' },
  { value: 'Europe/Berlin', label: '(UTC+2) Berlin' },
  { value: 'Europe/Bratislava', label: '(UTC+2) Bratislava' },
  { value: 'Europe/Brussels', label: '(UTC+2) Brussels' },
  { value: 'Europe/Budapest', label: '(UTC+2) Budapest' },
  { value: 'Europe/Copenhagen', label: '(UTC+2) Copenhagen' },
  { value: 'Africa/Johannesburg', label: '(UTC+2) Johannesburg' },
  { value: 'Europe/Luxembourg', label: '(UTC+2) Luxembourg' },
  { value: 'Europe/Madrid', label: '(UTC+2) Madrid' },
  { value: 'Europe/Malta', label: '(UTC+2) Malta' },
  { value: 'Europe/Oslo', label: '(UTC+2) Oslo' },
  { value: 'Europe/Paris', label: '(UTC+2) Paris' },
  { value: 'Europe/Prague', label: '(UTC+2) Prague' },
  { value: 'Europe/Rome', label: '(UTC+2) Rome' },
  { value: 'Europe/Stockholm', label: '(UTC+2) Stockholm' },
  { value: 'Europe/Vienna', label: '(UTC+2) Vienna' },
  { value: 'Europe/Warsaw', label: '(UTC+2) Warsaw' },
  { value: 'Europe/Zurich', label: '(UTC+2) Zurich' },
  { value: 'Europe/Athens', label: '(UTC+3) Athens' },
  { value: 'Asia/Bahrain', label: '(UTC+3) Bahrain' },
  { value: 'Europe/Bucharest', label: '(UTC+3) Bucharest' },
  { value: 'Africa/Cairo', label: '(UTC+3) Cairo' },
  { value: 'Europe/Helsinki', label: '(UTC+3) Helsinki' },
  { value: 'Europe/Istanbul', label: '(UTC+3) Istanbul' },
  { value: 'Asia/Jerusalem', label: '(UTC+3) Jerusalem' },
  { value: 'Asia/Kuwait', label: '(UTC+3) Kuwait' },
  { value: 'Europe/Moscow', label: '(UTC+3) Moscow' },
  { value: 'Africa/Nairobi', label: '(UTC+3) Nairobi' },
  { value: 'Asia/Nicosia', label: '(UTC+3) Nicosia' },
  { value: 'Asia/Qatar', label: '(UTC+3) Qatar' },
  { value: 'Europe/Riga', label: '(UTC+3) Riga' },
  { value: 'Asia/Riyadh', label: '(UTC+3) Riyadh' },
  { value: 'Europe/Tallinn', label: '(UTC+3) Tallinn' },
  { value: 'Europe/Vilnius', label: '(UTC+3) Vilnius' },
  { value: 'Asia/Tehran', label: '(UTC+3:30) Tehran' },
  { value: 'Asia/Dubai', label: '(UTC+4) Dubai' },
  { value: 'Asia/Muscat', label: '(UTC+4) Muscat' },
  { value: 'Asia/Kabul', label: '(UTC+4:30) Kabul' },
  { value: 'Asia/Ashgabat', label: '(UTC+5) Ashgabat' },
  { value: 'Asia/Karachi', label: '(UTC+5) Karachi' },
  { value: 'Asia/Colombo', label: '(UTC+5:30) Colombo' },
  { value: 'Asia/Kolkata', label: '(UTC+5:30) Kolkata' },
  { value: 'Asia/Kathmandu', label: '(UTC+5:45) Kathmandu' },
  { value: 'Asia/Dhaka', label: '(UTC+6) Dhaka' },
  { value: 'Asia/Yangon', label: '(UTC+6:30) Yangon' },
  { value: 'Asia/Bangkok', label: '(UTC+7) Bangkok' },
  { value: 'Asia/Ho_Chi_Minh', label: '(UTC+7) Ho Chi Minh' },
  { value: 'Asia/Jakarta', label: '(UTC+7) Jakarta' },
  { value: 'Asia/Chongqing', label: '(UTC+8) Chongqing' },
  { value: 'Asia/Hong_Kong', label: '(UTC+8) Hong Kong' },
  { value: 'Asia/Kuala_Lumpur', label: '(UTC+8) Kuala Lumpur' },
  { value: 'Asia/Manila', label: '(UTC+8) Manila' },
  { value: 'Australia/Perth', label: '(UTC+8) Perth' },
  { value: 'Asia/Shanghai', label: '(UTC+8) Shanghai' },
  { value: 'Asia/Singapore', label: '(UTC+8) Singapore' },
  { value: 'Asia/Taipei', label: '(UTC+8) Taipei' },
  { value: 'Asia/Seoul', label: '(UTC+9) Seoul' },
  { value: 'Asia/Tokyo', label: '(UTC+9) Tokyo' },
  { value: 'Australia/Brisbane', label: '(UTC+10) Brisbane' },
  { value: 'Australia/Adelaide', label: '(UTC+10:30) Adelaide' },
  { value: 'Australia/Sydney', label: '(UTC+11) Sydney' },
  { value: 'Pacific/Norfolk', label: '(UTC+12) Norfolk Island' },
  { value: 'Pacific/Auckland', label: '(UTC+13) New Zealand' },
  { value: 'Pacific/Fakaofo', label: '(UTC+13) Tokelau' },
  { value: 'Pacific/Chatham', label: '(UTC+13:45) Chatham Islands' },
] as const satisfies readonly TimezoneOption[];
